import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import { ApiError, type FollowUserDto } from "@tabipla/shared";
import { userProfileKeys } from "./useUserProfile";

// Query key factory
const followKeys = {
  all: ["mekke-follow"] as const,
  followers: (userId: string) => [...followKeys.all, "followers", userId] as const,
  followerList: (userId: string, params: { limit?: number; offset?: number }) =>
    [...followKeys.followers(userId), params] as const,
  following: (userId: string) => [...followKeys.all, "following", userId] as const,
  followingList: (userId: string, params: { limit?: number; offset?: number }) =>
    [...followKeys.following(userId), params] as const,
};

interface UseFollowListOptions {
  limit?: number;
  offset?: number;
}

// Fetch user's followers
export function useFollowers(userId: string | undefined, options: UseFollowListOptions = {}) {
  const { limit = 20, offset = 0 } = options;

  return useQuery({
    queryKey: followKeys.followerList(userId ?? "", { limit, offset }),
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");
      const params = new URLSearchParams();
      if (limit) params.set("limit", limit.toString());
      if (offset) params.set("offset", offset.toString());

      const endpoint = `/api/mekke/users/${userId}/followers${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await apiClient.get<FollowUserDto[]>(endpoint);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch followers");
      }
      return response.data ?? [];
    },
    enabled: !!userId,
  });
}

// Fetch users that this user is following
export function useFollowing(userId: string | undefined, options: UseFollowListOptions = {}) {
  const { limit = 20, offset = 0 } = options;

  return useQuery({
    queryKey: followKeys.followingList(userId ?? "", { limit, offset }),
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");
      const params = new URLSearchParams();
      if (limit) params.set("limit", limit.toString());
      if (offset) params.set("offset", offset.toString());

      const endpoint = `/api/mekke/users/${userId}/following${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await apiClient.get<FollowUserDto[]>(endpoint);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch following");
      }
      return response.data ?? [];
    },
    enabled: !!userId,
  });
}

// Follow a user
export function useFollowUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiClient.post<{ following: boolean }>(
        `/api/mekke/users/${userId}/follow`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to follow user");
      }
      return { userId, following: true };
    },
    onSuccess: (data) => {
      // Invalidate the user profile to update isFollowing status
      queryClient.invalidateQueries({ queryKey: userProfileKeys.profile(data.userId) });
      // Invalidate follower/following lists
      queryClient.invalidateQueries({ queryKey: followKeys.all });
    },
  });
}

// Unfollow a user
export function useUnfollowUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiClient.delete<{ following: boolean }>(
        `/api/mekke/users/${userId}/follow`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to unfollow user");
      }
      return { userId, following: false };
    },
    onSuccess: (data) => {
      // Invalidate the user profile to update isFollowing status
      queryClient.invalidateQueries({ queryKey: userProfileKeys.profile(data.userId) });
      // Invalidate follower/following lists
      queryClient.invalidateQueries({ queryKey: followKeys.all });
    },
  });
}
